// Auto-generated by splitter.py — do not regenerate; edit directly.

// UUID / uuidv7 (e.g. 019e5f32-fb3b-7000-ab66-e2a641bfff5c). Raw uuids in the UI
// read as machine noise — shorten them git-style. Human refs (ORG-2018, 7JL9GRA3R25,
// TXN-…) are left untouched.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A clean, scannable short form of an id: LAST 8 chars of a UUID, uppercased
 * (uuidv7s share a timestamp prefix, so the tail is what distinguishes them);
 * human refs pass through as-is. Render in a font-mono span. */
export const shortId = (value: string | number | null | undefined): string => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  return UUID_RE.test(s) ? s.slice(-8).toLocaleUpperCase() : s;
};

/** True when the value is a raw UUID (so callers can decide whether to offer "copy full"). */
export const isUuid = (value: string | number | null | undefined): boolean =>
  UUID_RE.test(String(value ?? '').trim());

/** Mask an email for display: keep the first char + tld, star the rest
 * (nickson@gmail.com → n***@gmail.com). Enough to recognise your OWN inbox
 * without printing the full address in a toast. Returns '' for a blank input. */
export const maskEmail = (value: string | null | undefined): string => {
  const s = String(value ?? '').trim();
  const at = s.indexOf('@');
  if (at < 1) return s;                       // no local part → nothing to mask
  return `${s[0]}***${s.slice(at)}`;
};

/** Order codes are compact base32 (e.g. GP3KZV29M869). Split once after the first
 * 4 characters (GP3K-ZV29M869) — a short prefix to anchor on, then the rest in one
 * block — so they're scannable without over-chunking. Purely cosmetic: anything that
 * already carries separators — UUIDs, ORG-2018, TXN-… — or a non-alphanumeric value
 * passes through untouched, and the RAW (undashed) value is still what gets stored /
 * searched / copied. */
export const formatOrderId = (value: string | number | null | undefined): string => {
  const s = String(value ?? '').trim();
  if (!s || isUuid(s) || !/^[A-Za-z0-9]+$/.test(s) || s.length <= 4) return s;
  return `${s.slice(0, 4)}-${s.slice(4)}`;
};

export const fmtUGX = (n: number | null | undefined): string => {
  if (n === 0 || n) {
    // UGX (and the other shilling currencies here) have no minor unit — round to
    // whole shillings so amounts don't render with stray decimals (e.g. the
    // net_amount generated column produced 1,187,563.28 / 783.086).
    return new Intl.NumberFormat('en-UG', { maximumFractionDigits: 0 }).format(n);
  }
  return '—';
};

// Currencies with no minor unit — render whole units, no decimals.
const ZERO_DECIMAL_CURRENCIES = new Set(['UGX', 'KES', 'TZS', 'RWF', 'BIF', 'SSP', 'XAF', 'XOF', 'JPY', 'KRW', 'VND']);

/** Plain integer with thousands separators — for COUNTS (orders, tickets), never money.
 * (fmtUGX was being misused for counts; use this so counts never gain a currency prefix.) */
export const fmtNum = (n: number | null | undefined): string =>
  (n === 0 || n) ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n) : '—';

/** Compact amount for tight KPI tiles — "3.57B", "51.5M", "10K", "445". Number only
 * (pair with a separate currency chip). '—' when no value. */
export const fmtCompact = (n: number | null | undefined): string =>
  (n === 0 || n) ? new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(n) : '—';

/** Currency-aware money: "{CUR} {amount}". The currency MUST come from the data.
 * Shilling/zero-decimal currencies render whole units; others (USD, EUR…) two
 * decimals. '—' when there is no value.
 *
 * NO FALLBACK CURRENCY, and no bare number either. An amount without its currency
 * is not a partial truth, it is a different fact: 3,200 is ~USD 0.85 in UGX, ~USD 25
 * in KES and USD 3,200 in USD. So an unknown currency returns '—' — the amount is
 * withheld along with it, because the two are ONE value, not two fields.
 *
 * This previously read `currency || 'UGX'`, directly contradicting the line above it:
 * mookh order PD5PPQQ3ZGZ3 (KES ticket, buyer chose USD, payment pending) rendered a
 * confident UGX that appeared nowhere in the transaction. */
/** Format one {label, value, fmt} KPI tile from the report engine's summary
 * (`getReportSummary`'s `kpis` array — see reportExecutor.ts). Currency-less by
 * design (a KPI carries no currency code): 'money' compacts to K/M/B, 'pct' adds
 * a % sign, anything else is a plain localized number. Matches the pattern already
 * used inline in ReportBuilderModal.jsx / ReportDetailDrawer.jsx — the ONE place
 * for it, so a new summary renderer doesn't reinvent (or mis-invent) a fourth copy. */
export const fmtKpi = (k: { value: number | string; fmt?: string } | null | undefined): string => {
  if (!k) return '—';
  const v = k.value;
  if (k.fmt === 'pct') return `${v}%`;
  if (k.fmt === 'money' && typeof v === 'number') {
    return v >= 1e9 ? `${(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}k` : v.toLocaleString();
  }
  return typeof v === 'number' ? v.toLocaleString() : String(v ?? '—');
};

export const fmtMoney = (n: number | null | undefined, currency?: string | null): string => {
  if (n !== 0 && !n) return '—';
  const cur = currency ? String(currency).toUpperCase() : '';
  if (!cur) return '—';
  const digits = ZERO_DECIMAL_CURRENCIES.has(cur) ? 0 : 2;
  return `${cur} ${new Intl.NumberFormat('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(n)}`;
};

/** Currency-aware COMPACT money: "{CUR} {compact}". Adapts magnitude across ALL
 * orders (K/M/B) via fmtCompact — so 10,450 → "UGX 10.45K" (NOT "UGX 0.0M") and
 * 3.44e9 → "UGX 3.44B". The single-magnitude "/1e6 → 0.0M" bug is structurally
 * impossible here. Currency MUST come from the data. '—' when no value. */
/** The AMOUNT only, with the decimal places that currency actually uses — no code.
 * For tables that state the currency once (a column header, a caption) instead of
 * repeating it on every row. Still currency-AWARE: it needs the code to know whether
 * 2 decimals are real money or noise, it just doesn't print it. Never call this
 * without a currency; that's what fmtMoney's '—' is for. */
export const fmtAmount = (n: number | null | undefined, currency?: string | null): string => {
  if (n !== 0 && !n) return '—';
  const cur = currency ? String(currency).toUpperCase() : '';
  const digits = ZERO_DECIMAL_CURRENCIES.has(cur) ? 0 : 2;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(n);
};

export const fmtMoneyCompact = (n: number | null | undefined, currency?: string | null): string => {
  if (n !== 0 && !n) return '—';
  // Same rule as fmtMoney: no currency means no number. See its comment for why a bare
  // amount is a different fact rather than a partial one.
  const cur = currency ? String(currency).toUpperCase() : '';
  if (!cur) return '—';
  return `${cur} ${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 0 }).format(n)}`;
};

type SmartTimeOpts = { seconds?: boolean; fallback?: string };

// Day-aware time. Renders just the clock (HH:MM) for a timestamp that falls on
// TODAY, but prefixes the date for any other day — so a value from another day
// never reads as if it were today's time. Crosses years → includes the year.
export const fmtSmartTime = (value: string | number | Date | null | undefined, { seconds = false, fallback = '—' }: SmartTimeOpts = {}): string => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  const now = new Date();
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}) });
  const sameDay = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
  if (sameDay) return time;
  const datePart = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }) });
  return `${datePart}, ${time}`;
};

/**
 * IS THIS REFERENCE HOLDING MONEY THAT WAS NEVER CREDITED?
 *
 * ONE definition, exported, because this fact was being written four different ways — a server
 * predicate, a tab badge, a red dot and a detail banner — and they disagreed about the same row:
 * a reference could sit on the worklist and show no banner when opened, or show the banner and
 * not be on the worklist.
 *
 * It compares the two columns the settlement writer maintains, and NOT `expected`: an attempt with
 * no recorded expectation is not an attempt that expected zero (498 of 810 prod attempts have a
 * null expectedAmount, 265 of them settled), and receipt COUNT is not money at all — one MookhPay
 * callback legitimately carries several charges that were all credited.
 *
 * The epsilon matches the server's `+ 0.01`, so a sub-cent float residue cannot light a red alarm
 * over an empty tab.
 */
export const overpaidBy = (t: { received?: number | null; credited?: number | null }): number | null => {
  if (t?.received == null || t?.credited == null) return null;
  const over = Number(t.received) - Number(t.credited);
  return over > 0.01 ? over : null;
};
export const isOverpaid = (t: { received?: number | null; credited?: number | null }): boolean =>
  overpaidBy(t) != null;

// ─────────────────────────────────────────────────────────────────────────────
// ORGANISER-AUTHORED RICH TEXT → one line of readable text.
//
// Descriptions (ticket type, event, venue, season-pass tier) are authored on the
// organiser side and reach this console in THREE formats, because the editor changed
// twice and the stored rows were never normalised:
//
//   1. raw HTML      — mookh v2 rows, carried across verbatim by the migration
//                      (migrate-mookh-v2-to-destination.ts:933 passes ticket_description
//                      through as a string, while :750 converts the EVENT description).
//   2. Lexical JSON  — the retired organiser editor, `{"root":{"children":[…]}}`.
//   3. markdown      — current, since ~July 2026.
//
// Rendering `{value}` in JSX escapes whatever it is, so format 1 shows its tags on
// screen (`<p><br></p>…`) and format 3 shows its asterisks. Only the SYMPTOM differs:
// markdown degrades gracefully and HTML does not, which is why this looked like it
// affected only some rows.
//
// This strips rather than renders, deliberately. The alternative — dangerouslySetInnerHTML —
// would execute organiser-authored markup inside a console holding refund and payout
// controls; there is no innerHTML anywhere in this codebase and this is not the place to
// introduce it. Entities are decoded from a fixed table AFTER tags are removed, so nothing
// decoded can re-introduce markup.
//
// Fixing the stored data is a separate, riskier job (it rewrites organisers' own copy).
// Until that happens the console meets all three shapes, so it normalises on read.
// ─────────────────────────────────────────────────────────────────────────────

const ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  hellip: '…', mdash: '—', ndash: '–', rsquo: '’', lsquo: '‘',
  rdquo: '”', ldquo: '“', middot: '·', bull: '•',
};

const decodeEntities = (s: string): string =>
  s.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (whole, body: string) => {
    if (body[0] === '#') {
      const cp = body[1]?.toLowerCase() === 'x'
        ? parseInt(body.slice(2), 16)
        : parseInt(body.slice(1), 10);
      // Ignore anything that isn't a sane codepoint rather than emitting U+FFFD.
      return Number.isFinite(cp) && cp > 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : whole;
    }
    return ENTITIES[body.toLowerCase()] ?? whole;
  });

/** Walk a Lexical tree and collect its text nodes in order. */
const lexicalText = (node: any): string => {
  if (!node || typeof node !== 'object') return '';
  if (node.type === 'text' && typeof node.text === 'string') return node.text;
  if (node.type === 'linebreak') return ' ';
  const kids = Array.isArray(node.children) ? node.children.map(lexicalText).join(' ') : '';
  // Block-level nodes need a separator or words weld together across paragraphs.
  return node.type === 'paragraph' || node.type === 'heading' || node.type === 'listitem'
    ? `${kids} `
    : kids;
};

/** Shared core: any of the three formats → text with a NEWLINE at every block boundary.
 *  Callers decide whether those breaks survive (a reading surface) or collapse (a cell). */
const toText = (value: unknown): string => {
  if (value == null) return '';
  let s = String(value);

  if (s.startsWith('{') && s.includes('"root"')) {
    try {
      const tree = JSON.parse(s);
      if (tree?.root) s = lexicalText(tree.root);
    } catch { /* not the JSON we hoped for — fall through and treat it as text */ }
  }

  s = s
    // Block boundaries become newlines FIRST, so "…flights.</p><p>No payment" cannot
    // weld into "…flights.No payment" once the tags are gone.
    .replace(/<(br|\/p|\/div|\/li|\/h[1-6]|\/tr|\/blockquote)\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '');

  return decodeEntities(s)
    // Markdown emphasis/heading marks are noise once nothing will render them.
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/(\*\*|__|[*_`])/g, '');
};

/** ONE LINE, for table cells, labels and tooltips. Pass `max` to ellipsise. */
export const plainText = (value: unknown, max?: number): string => {
  const s = toText(value).replace(/\s+/g, ' ').trim();
  if (max && s.length > max) return `${s.slice(0, max - 1).trimEnd()}…`;
  return s;
};

/** PARAGRAPHS PRESERVED, for surfaces whose job is reading the description. Render with
 *  `whitespace-pre-line`; collapsing this to one line would turn a structured description
 *  into a wall of text, which is a different kind of broken from the one being fixed. */
export const plainTextBlock = (value: unknown): string =>
  toText(value)
    .replace(/[ \t]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')   // editors emit <p><br></p> runs; one blank line is enough
    .trim();
